import _ from 'lodash';
import { SecureContext } from 'scbk-services/types/shared-context/secure-context';

/**
 * 보안 컨텍스트를 포함한 최종 요청 데이터 타입
 * - common: 전자서명/보안매체 등 공통 보안영역
 * - body: 실제 API 개별 요청 DTO
 */
type SecureWrappedRequest<T = unknown> = {
    common?: SecureContext.Context;
    body: T;
};

/**
 * 객체 내부의 undefined 값을 재귀적으로 제거한다.
 * - SecureContext는 선택적 필드가 많아 그대로 전송 시 undefined 필드가 payload에 섞일 수 잇음
 * - 서버 전문 파싱/검증 안정성을 위해 사전에 정리한다.
 * @param obj 임의의 객체 / 배열
 * @returns undefined 값이 제거된 새로운 객체
 */
const omitDeepUndefined = <T>(obj: T): T => {
    if (_.isArray(obj)) {
        return obj.map(omitDeepUndefined) as any;
    }

    if (!_.isPlainObject(obj)) {
        return obj;
    }

    const output: any = {};
    Object.entries(obj as any).forEach(([k, v]) => {
        if (_.isUndefined(v)) {
            return;
        }

        const next = omitDeepUndefined(v);
        output[k] = next;
    });
    return output;
};

/**
 * SecureContext를 포함한 최종 요청 payload를 생성한다.
 *
 * - SecureContext가 없거나(sign/tokens 모두 비어있으면) -> 기존 요청 DTO 그대로 전송
 * - SecureContext가 존재하면 -> { common, body } 구조로 래핑하여 전송
 *
 * @param data 실제 API 요청 DTO
 * @param context SecureContextStore 에서 가져온 보안 컨텍스트
 */
const buildSecureWrappedRequest = <T>(
    data: T,
    context: SecureContext.Context,
): SecureWrappedRequest | any => {
    const cleanedCtx = context ? omitDeepUndefined(context) : undefined;

    //*********************************************************
    //[2026.11.04 최성돈] 요청 데이터를 일반 객체로 정규화 처리 추가
    //*********************************************************
    const normalizedData = normalizeData(data);
    const hasSign = !_.isEmpty(cleanedCtx?.sign);
    const hasTokens = !_.isEmpty(cleanedCtx?.tokens);
    const hasKeypad = !_.isEmpty(cleanedCtx?.keypad) || !_.isEmpty(cleanedCtx?.subKeypad);

    if (!hasSign && !hasTokens && !hasKeypad) {
        return { ...normalizedData };
    }

    return {
        common: cleanedCtx,
        body: { ...normalizedData },
    };
};

/**
 * 요청 데이터를 일반 객체로 정규화한다.
 *
 * - 일반: 객체: 그대로 반환
 * - JSON 문자열: JSON.parse 후 반환
 * - 잘못된 문자열 또는 객체가 아닌 값: 예외발생
 * @param data
 * @returns
 */
const normalizeData = <T>(data: T): Record<string, unknown> => {
    let normalized: unknown = data;

    if (typeof data === 'string') {
        console.error('[secureContext] Unexpected string request data', {
            type: typeof data,
            length: data.length,
            stack: new Error().stack,
        });

        try {
            normalized = JSON.parse(data);
        } catch {
            throw new Error('[secureContext] Invalid JSON request data');
        }

        if (normalized === null || typeof normalized !== 'object' || Array.isArray(data)) {
            throw new Error('[secureContext] Request data must be an object');
        }
    }

    return normalized as Record<string, unknown>;
};

export { buildSecureWrappedRequest, omitDeepUndefined, type SecureWrappedRequest };
